import type { Listener } from '@/common/composables/usePageData'
import { getListenerEventNamesToMatch } from '@/components/platform/events/event-map'

export function resolvePageEventListeners(
  pagesData: Record<string, unknown>,
  options: {
    pageDataSlot?: string
    registryFormId?: string
    injectedListeners?: Listener[]
  },
): Listener[] {
  const slot = String(options.pageDataSlot ?? '').trim()
  if (slot) {
    const fromSlot = (pagesData[slot] as { jsResult?: { event_listeners?: Listener[] } } | undefined)
      ?.jsResult?.event_listeners
    if (Array.isArray(fromSlot) && fromSlot.length > 0) {
      return fromSlot
    }
  }

  const registryFormId = String(options.registryFormId ?? '').trim()
  if (registryFormId) {
    const fromRegistry = (
      pagesData[registryFormId] as { jsResult?: { event_listeners?: Listener[] } } | undefined
    )?.jsResult?.event_listeners
    if (Array.isArray(fromRegistry) && fromRegistry.length > 0) {
      return fromRegistry
    }
  }

  return options.injectedListeners ?? []
}

export function filterEventListeners(
  listeners: Listener[],
  level: string,
  id: number | string,
  eventName?: string,
): Listener[] {
  const numericId = typeof id === 'string' ? parseInt(id, 10) : id
  if (Number.isNaN(numericId) && level === 'element') return []

  const levelNorm = String(level ?? '').toLowerCase()
  const eventNamesToMatch = eventName ? getListenerEventNamesToMatch(eventName) : null

  return listeners.filter((listener) => {
    if (listener.to_remove) return false
    const typeMatch = String(listener.level ?? '').toLowerCase() === levelNorm
    const listenerIdNum = listener.id != null ? Number(listener.id) : NaN
    const idMatch =
      listenerIdNum === numericId || listener.id === id || listener.id === numericId
    const ev = String(listener.event_name ?? '')
    const eventMatch = eventNamesToMatch
      ? eventNamesToMatch.some((n) => String(n).toLowerCase() === ev.toLowerCase())
      : true
    return typeMatch && idMatch && eventMatch
  })
}

function listenerMergeKey(listener: Listener): string {
  return [
    String(listener.level ?? '').toLowerCase(),
    String(listener.id ?? ''),
    String(listener.event_name ?? '').toLowerCase(),
    String(listener.name ?? ''),
    String(listener.function ?? ''),
  ].join('|')
}

export function mergeEventListenersForPageUpdate(
  previous: Listener[] | null | undefined,
  incoming: Listener[] | null | undefined,
): Listener[] | undefined {
  const prev = Array.isArray(previous) ? previous : []
  if (!Array.isArray(incoming)) {
    return previous ?? undefined
  }
  if (incoming.length === 0) {
    return prev.length > 0 ? prev : incoming
  }

  const merged = new Map<string, Listener>()
  for (const listener of prev) {
    merged.set(listenerMergeKey(listener), listener)
  }

  for (const listener of incoming) {
    const key = listenerMergeKey(listener)
    if (listener.to_remove) {
      merged.delete(key)
      continue
    }
    merged.set(key, listener)
  }

  return [...merged.values()]
}
